import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Container,
  FormControlLabel,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
  CircularProgress,
} from '@mui/material';
import {
  Save as SaveIcon,
  Refresh as RefreshIcon,
  LocalOffer as CouponIcon,
  Campaign as AdIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { toast } from 'react-toastify';
import DashboardLayout from '../../layouts/DashboardLayout';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';

/**
 * DigiDrop Coupon Flow Control
 * Dynamically set how many shared Mitra coupons citizens get per ad type.
 * Unique Locsy lever: engagement-gated local commerce rewards with privacy-safe publisher contact.
 */
const CouponFlow = () => {
  const [rules, setRules] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const authHeaders = () => {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/business-posts/coupon-flow/rules`, {
        headers: authHeaders(),
      });
      if (res.data?.success) {
        setRules(res.data.data?.rules || []);
        setStats(res.data.data?.stats || null);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load Coupon Flow');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    document.title = 'Coupon Flow (DigiDrop) - LOCSY';
    load();
  }, [load]);

  const updateRule = (placementKey, patch) => {
    setRules((prev) =>
      prev.map((r) =>
        r.placementKey === placementKey ? { ...r, ...patch } : r,
      ),
    );
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await axios.put(
        `${API_BASE_URL}/business-posts/coupon-flow/rules`,
        { rules },
        { headers: authHeaders() },
      );
      if (res.data?.success) {
        setRules(res.data.data?.rules?.length ? res.data.data.rules : rules);
        setStats(res.data.data?.stats || stats);
        toast.success('Coupon Flow saved');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const summary = useMemo(() => {
    const enabled = rules.filter((r) => r.enabled).length;
    return { enabled, total: rules.length };
  }, [rules]);

  return (
    <DashboardLayout>
      <Container maxWidth="lg" sx={{ py: 3 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={2} mb={2}>
          <Box>
            <Typography variant="h4" fontWeight={800} gutterBottom>
              Coupon Flow · DigiDrop
            </Typography>
            <Typography color="text.secondary" maxWidth={640}>
              Control how many Mitra “Share with Locsy” coupons citizens unlock when they check
              popups, digital pamphlets, and status/updates — without exposing full publisher phones.
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <Button startIcon={<RefreshIcon />} onClick={load} disabled={loading}>
              Refresh
            </Button>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleSave}
              disabled={saving || loading}
            >
              {saving ? 'Saving…' : 'Save rules'}
            </Button>
          </Stack>
        </Stack>

        <Alert severity="info" sx={{ mb: 2 }}>
          <strong>Live-safe:</strong> existing ad feeds, coupon assign/redeem, and Mitra create flows
          stay unchanged. DigiDrop only uses coupons marked <em>Share with Locsy</em>, with daily
          caps and one grant per ad per citizen per day (IST). Related:{' '}
          <a href="/content/lucky-wheel">Locsy Spin</a> uses the same pool for listing-publish
          rewards.
        </Alert>

        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} mb={3}>
          <Paper sx={{ p: 2, flex: 1 }}>
            <Stack direction="row" spacing={1} alignItems="center" mb={1}>
              <CouponIcon color="primary" />
              <Typography fontWeight={700}>Shared pool</Typography>
            </Stack>
            <Typography variant="h4">{stats?.poolSize ?? '—'}</Typography>
            <Typography variant="body2" color="text.secondary">
              Active templates in DigiDrop
            </Typography>
          </Paper>
          <Paper sx={{ p: 2, flex: 1 }}>
            <Stack direction="row" spacing={1} alignItems="center" mb={1}>
              <AdIcon color="primary" />
              <Typography fontWeight={700}>Grants today</Typography>
            </Stack>
            <Typography variant="h4">{stats?.grantsToday ?? '—'}</Typography>
            <Typography variant="body2" color="text.secondary">
              {stats?.couponsIssuedToday ?? 0} coupons issued · {stats?.grantDate || 'IST day'}
            </Typography>
          </Paper>
          <Paper sx={{ p: 2, flex: 1 }}>
            <Typography fontWeight={700} mb={1}>
              Placements live
            </Typography>
            <Typography variant="h4">
              {summary.enabled}/{summary.total}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Enabled DigiDrop surfaces
            </Typography>
          </Paper>
        </Stack>

        {loading ? (
          <Box display="flex" justifyContent="center" py={6}>
            <CircularProgress />
          </Box>
        ) : (
          <Stack spacing={2}>
            {rules.map((rule) => (
              <Paper key={rule.placementKey} sx={{ p: 2.5 }}>
                <Stack
                  direction={{ xs: 'column', md: 'row' }}
                  spacing={2}
                  alignItems={{ md: 'center' }}
                  justifyContent="space-between"
                >
                  <Box flex={1}>
                    <Stack direction="row" spacing={1} alignItems="center" mb={0.5}>
                      <Typography variant="h6" fontWeight={700}>
                        {rule.label}
                      </Typography>
                      <Chip size="small" label={rule.placementKey} />
                      {!rule.enabled && <Chip size="small" color="default" label="Off" />}
                    </Stack>
                    <Typography variant="body2" color="text.secondary">
                      {rule.notes ||
                        'Citizens who engage this ad type unlock random shared coupons (same-business first, then town, then pool).'}
                    </Typography>
                  </Box>

                  <FormControlLabel
                    control={
                      <Switch
                        checked={!!rule.enabled}
                        onChange={(e) =>
                          updateRule(rule.placementKey, { enabled: e.target.checked })
                        }
                      />
                    }
                    label="Enabled"
                  />

                  <TextField
                    label="Coupons / engage"
                    type="number"
                    size="small"
                    value={rule.couponsPerEngage}
                    onChange={(e) =>
                      updateRule(rule.placementKey, {
                        couponsPerEngage: Math.max(0, Math.min(10, Number(e.target.value) || 0)),
                      })
                    }
                    inputProps={{ min: 0, max: 10 }}
                    sx={{ width: 140 }}
                    helperText="e.g. popup 2, pamphlet 1"
                  />

                  <TextField
                    label="Pool %"
                    type="number"
                    size="small"
                    value={rule.poolSharePercent ?? 0}
                    onChange={(e) =>
                      updateRule(rule.placementKey, {
                        poolSharePercent: Math.max(
                          0,
                          Math.min(100, Number(e.target.value) || 0),
                        ),
                      })
                    }
                    inputProps={{ min: 0, max: 100 }}
                    sx={{ width: 110 }}
                    helperText="Share of DigiDrop pool"
                  />

                  <TextField
                    label="Daily citizen cap"
                    type="number"
                    size="small"
                    value={rule.dailyCitizenCap}
                    onChange={(e) =>
                      updateRule(rule.placementKey, {
                        dailyCitizenCap: Math.max(0, Math.min(50, Number(e.target.value) || 0)),
                      })
                    }
                    inputProps={{ min: 0, max: 50 }}
                    sx={{ width: 150 }}
                    helperText="Anti-spam ceiling"
                  />
                </Stack>

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} mt={1.5}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={rule.preferSameBusiness !== false}
                        onChange={(e) =>
                          updateRule(rule.placementKey, {
                            preferSameBusiness: e.target.checked,
                          })
                        }
                      />
                    }
                    label="Prefer publisher’s own shared coupons"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={rule.preferSameTown !== false}
                        onChange={(e) =>
                          updateRule(rule.placementKey, {
                            preferSameTown: e.target.checked,
                          })
                        }
                      />
                    }
                    label="Prefer same-town pool"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={rule.requireLogin !== false}
                        onChange={(e) =>
                          updateRule(rule.placementKey, {
                            requireLogin: e.target.checked,
                          })
                        }
                      />
                    }
                    label="Require login to claim"
                  />
                </Stack>
              </Paper>
            ))}
          </Stack>
        )}

        {!!stats?.byPlacement?.length && (
          <Paper sx={{ p: 2, mt: 3 }}>
            <Typography fontWeight={700} mb={1}>
              Today by placement
            </Typography>
            <Stack direction="row" flexWrap="wrap" gap={1}>
              {stats.byPlacement.map((row) => (
                <Chip
                  key={row.placement}
                  label={`${row.placement}: ${row.grants} grants · ${row.coupons} coupons`}
                />
              ))}
            </Stack>
          </Paper>
        )}
      </Container>
    </DashboardLayout>
  );
};

export default CouponFlow;
